/**
 * useProjectSchedule — the single source of truth for a project's tasks.
 *
 * Every view (Gantt grid, bars, Calendar) reads and mutates through this hook.
 * The old code derived dates in three separate places and they drifted apart;
 * there is exactly one derivation now, and it lives in scheduleAdapter.
 *
 * Behaviour:
 *   • optimistic — the UI updates immediately, so typing feels instant
 *   • debounced  — rapid edits to one task collapse into a single PATCH
 *   • rollback   — a failed save restores the last known server value AND says so
 *
 * Silent failure is the thing to avoid here: with autosave and no Save button,
 * a swallowed error means the user believes work is saved when it isn't.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, apiErrorMessage } from '../lib/api';

export const SAVE_DEBOUNCE_MS = 1500;   // matches Cost Estimate's autosave feel

// ── Pure list operations (exported for testing) ──────────────────────────────

/** Merge a partial patch into one task. Returns a new array. */
export function applyPatchToList(tasks, taskId, patch) {
  return tasks.map(t => (t.id === taskId ? { ...t, ...patch } : t));
}

/** Append, keeping sort_order monotonic. */
export function insertTask(tasks, task) {
  const nextOrder = tasks.length
    ? Math.max(...tasks.map(t => t.sort_order ?? 0)) + 1
    : 0;
  return [...tasks, { sort_order: nextOrder, ...task }];
}

export function removeTaskFromList(tasks, taskId) {
  return tasks.filter(t => t.id !== taskId);
}

/** Rewrite sort_order from an explicit id order; unlisted tasks keep trailing. */
export function applyReorder(tasks, orderedIds) {
  const pos = new Map(orderedIds.map((id, i) => [id, i]));
  return [...tasks]
    .map(t => ({ ...t, sort_order: pos.has(t.id) ? pos.get(t.id) : orderedIds.length + (t.sort_order ?? 0) }))
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function sortTasks(tasks) {
  return [...tasks].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/** Merge queued patches for one task; later values win. */
export function mergePatches(existing, incoming) {
  return { ...(existing || {}), ...incoming };
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export default function useProjectSchedule(projectId) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Last value the server confirmed, per task id — the rollback target.
  const serverStateRef = useRef(new Map());
  // Queued partial patches awaiting flush, per task id.
  const pendingRef = useRef(new Map());
  const timersRef = useRef(new Map());

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}`);
      const list = sortTasks(res.data?.tasks || []);
      setTasks(list);
      serverStateRef.current = new Map(list.map(t => [t.id, t]));
    } catch (err) {
      console.error('Failed to load schedule:', err);
      toast.error(apiErrorMessage(err, 'Could not load the schedule.'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const flushTask = useCallback(async (taskId) => {
    const patch = pendingRef.current.get(taskId);
    if (!patch) return;
    pendingRef.current.delete(taskId);
    timersRef.current.delete(taskId);

    setSaving(true);
    try {
      const res = await axios.patch(`${API}/projects/${projectId}/tasks/${taskId}`, patch);
      const saved = res.data;
      serverStateRef.current.set(taskId, saved);
      // Re-sync from the server response so any server-side normalisation shows.
      setTasks(prev => applyPatchToList(prev, taskId, saved));
    } catch (err) {
      console.error('Task save failed:', err);
      const previous = serverStateRef.current.get(taskId);
      if (previous) {
        setTasks(prev => applyPatchToList(prev, taskId, previous));
      }
      toast.error(apiErrorMessage(err, 'Could not save that change — it has been undone.'));
    } finally {
      setSaving(false);
    }
  }, [projectId]);

  /** Optimistically apply a patch, then save it (debounced per task). */
  const updateTask = useCallback((taskId, patch) => {
    setTasks(prev => applyPatchToList(prev, taskId, patch));
    pendingRef.current.set(taskId, mergePatches(pendingRef.current.get(taskId), patch));

    const existing = timersRef.current.get(taskId);
    if (existing) clearTimeout(existing);
    timersRef.current.set(taskId, setTimeout(() => flushTask(taskId), SAVE_DEBOUNCE_MS));
  }, [flushTask]);

  const addTask = useCallback(async (partial = {}) => {
    const optimisticId = `tmp_${crypto.randomUUID()}`;
    const draft = { id: optimisticId, task_name: '', trade: '', status: 'pending', start_date: '', end_date: '', ...partial };
    setTasks(prev => sortTasks(insertTask(prev, draft)));

    try {
      const res = await axios.post(`${API}/projects/${projectId}/tasks`, partial);
      const created = res.data;
      serverStateRef.current.set(created.id, created);
      // Swap the temporary row for the server's, keeping position.
      setTasks(prev => sortTasks(prev.map(t => (t.id === optimisticId ? created : t))));
      return created;
    } catch (err) {
      console.error('Task create failed:', err);
      setTasks(prev => removeTaskFromList(prev, optimisticId));
      toast.error(apiErrorMessage(err, 'Could not add that task.'));
      return null;
    }
  }, [projectId]);

  const deleteTask = useCallback(async (taskId) => {
    const snapshot = tasks;
    setTasks(prev => removeTaskFromList(prev, taskId));
    try {
      await axios.delete(`${API}/projects/${projectId}/tasks/${taskId}`);
      serverStateRef.current.delete(taskId);
    } catch (err) {
      console.error('Task delete failed:', err);
      setTasks(snapshot);                    // put it back — deletion did not happen
      toast.error(apiErrorMessage(err, 'Could not delete that task — it has been restored.'));
    }
  }, [projectId, tasks]);

  const reorderTasks = useCallback(async (orderedIds) => {
    const snapshot = tasks;
    setTasks(prev => applyReorder(prev, orderedIds));
    try {
      await axios.patch(`${API}/projects/${projectId}/tasks/reorder`, { task_ids: orderedIds });
    } catch (err) {
      console.error('Reorder failed:', err);
      setTasks(snapshot);
      toast.error(apiErrorMessage(err, 'Could not reorder tasks — the previous order has been restored.'));
    }
  }, [projectId, tasks]);

  /** Flush everything now — call before navigating away. */
  const flushAll = useCallback(async () => {
    const ids = [...pendingRef.current.keys()];
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
    await Promise.all(ids.map(flushTask));
  }, [flushTask]);

  // Pending edits must not die with the component.
  useEffect(() => {
    const timers = timersRef.current;
    return () => { timers.forEach(clearTimeout); };
  }, []);

  return { tasks, loading, saving, addTask, updateTask, deleteTask, reorderTasks, flushAll, reload: load };
}
